import { createContext } from "react";

type HostListener = (height: number) => void;

/**
 * What the top bar's toolbar row paints, which the shell's status band and the shared header fade
 * follow so the three always read as one surface: `"chrome"` is the app's muted chrome (the sky
 * while the space background is on), `"page"` the page's own `bg-background`, and `"surface"`
 * the white `bg-surface` used above a Learn summary sheet.
 */
export type PrimaryControlsStripToolbarSurface = "chrome" | "page" | "surface";

export type PrimaryControlsStripToolbarHeightRegistry = {
  /** The tallest height a FOCUSED toolbar row currently declares, or `0` when none is mounted. */
  getCurrent: () => number;
  /** The tallest focused row's surface; `"chrome"` when none is mounted. */
  getSurface: () => PrimaryControlsStripToolbarSurface;
  /** Whether a FOCUSED toolbar is mounted at all (a vertical one declares height `0`). */
  hasToolbar: () => boolean;
  /**
   * Whether the tallest focused row floats as glass over the page scroller (iOS 26), which then
   * runs beneath the status bar too: the shell paints neither a status band nor the header fade.
   */
  isFloating: () => boolean;
  /** Declare a row; the returned function withdraws it (unmount or blur). Idempotent. */
  publish: (
    height: number,
    surface?: PrimaryControlsStripToolbarSurface,
    floating?: boolean,
  ) => () => void;
  subscribe: (listener: HostListener) => () => void;
};

/**
 * Tracks how tall the opaque toolbar row under the status bar currently is, so the shared header
 * fade in `NavLayout` can start exactly at the bottom border of the focused screen's toolbar
 * buttons — fully opaque there, dissolving downward over the content that scrolls under it.
 *
 * Screens that paint no toolbar publish nothing and the registry reports `0`, which puts the
 * fade directly below the safe area. Before this existed the fade assumed a hard-coded 48pt row
 * on every `(app)` tab, so a tab without one (Settings) drew its gradient adrift in the middle of
 * scrolling content — a hairline crossing the first card rather than a fade.
 *
 * Two rules keep the offset honest, and both exist because of a shipped glitch:
 *
 * 1. **Only FOCUSED toolbars may publish** (enforced by `PrimaryControlsStripToolbar`, not here). Inactive
 *    bottom tabs stay mounted, and the Deck / Stats toolbars are 9pt taller than the rest (they
 *    trade the fade for solid spacing). With every mounted row counted, opening Deck once left
 *    its taller entry in the registry, so Browse's fade began 9pt BELOW its pills: a strip of
 *    sharp tiles under the buttons, then a hard line where the band's opaque edge started, then
 *    the dissolve. "Tallest wins" is only correct when every entry belongs to the screen on
 *    screen.
 *
 * 2. **The offset is COMPUTED, never measured.** The fade sits at `insets.top + height`, which
 *    holds because every `(app)` toolbar is the first thing under the shell's safe-area spacer.
 *    Measuring the row's real window edge instead (`measureInWindow`) was tried and reverted: on
 *    a cold start the row is measured in the commit BEFORE the native safe-area spacer above it
 *    has applied its inset, so it reports its bottom 47pt too high, and nothing ever corrects it —
 *    a view's `onLayout` does not fire when only an ancestor moves. The fade then painted OVER
 *    the toolbar. A synchronous, inset-based offset cannot go stale that way.
 */
export function createPrimaryControlsStripToolbarHeightRegistry(): PrimaryControlsStripToolbarHeightRegistry {
  const entries = new Map<
    number,
    { floating: boolean; height: number; surface: PrimaryControlsStripToolbarSurface }
  >();
  let nextId = 0;
  const listeners = new Set<HostListener>();
  let current = 0;
  let currentSurface: PrimaryControlsStripToolbarSurface = "chrome";
  let currentHasToolbar = false;
  let currentFloating = false;

  const recompute = () => {
    let tallest = 0;
    let surface: PrimaryControlsStripToolbarSurface = "chrome";
    let floating = false;
    for (const entry of entries.values()) {
      if (entry.height > tallest) {
        tallest = entry.height;
        surface = entry.surface;
        floating = entry.floating;
      }
    }
    const hasToolbar = entries.size > 0;
    if (
      tallest === current &&
      surface === currentSurface &&
      hasToolbar === currentHasToolbar &&
      floating === currentFloating
    ) {
      return;
    }
    current = tallest;
    currentSurface = surface;
    currentHasToolbar = hasToolbar;
    currentFloating = floating;
    for (const listener of listeners) {
      listener(current);
    }
  };

  return {
    getCurrent: () => current,
    getSurface: () => currentSurface,
    hasToolbar: () => currentHasToolbar,
    isFloating: () => currentFloating,
    publish(height, surface = "chrome", floating = false) {
      const id = ++nextId;
      entries.set(id, { floating, height, surface });
      recompute();
      return () => {
        if (entries.delete(id)) {
          recompute();
        }
      };
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/**
 * Publishing a toolbar height is an enhancement to the shared header fade, never a requirement for
 * a toolbar to render. A `PrimaryControlsStripToolbar` mounted outside `NavLayout` — an isolated render test,
 * Storybook, the landscape sidebar shell — therefore falls back to this inert registry and paints
 * its row normally, rather than throwing the way the title/back contexts do. There is no fade to
 * align in those trees, so there is nothing for a missing provider to break.
 */
const INERT_REGISTRY: PrimaryControlsStripToolbarHeightRegistry = {
  getCurrent: () => 0,
  getSurface: () => "chrome",
  hasToolbar: () => false,
  isFloating: () => false,
  publish: () => () => undefined,
  subscribe: () => () => undefined,
};

export const PrimaryControlsStripToolbarHeightContext =
  createContext<PrimaryControlsStripToolbarHeightRegistry>(INERT_REGISTRY);
